//////////////////
// IMPORTS
//////////////////

// libraries
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { createRefetchContainer, RelayRefetchProp } from "react-relay";
import { cloneDeep, debounce, merge } from "lodash";

// onboarding-org-invite imports
import { fragments, refetchQuery } from "./OnboardingOrgInvite.gql";
import {
  getAugmentedPersons,
  isAtLeastOnePersonNotSelectedAndNotUser,
  sortPersons,
} from "./OnboardingOrgInvite.util";
// other internals
import { CopyPersonalInviteLinkBtn } from "@components/copy-personal-invite-link-btn";
import { OrgInviteCheckbox } from "../org-invite-checkbox";
import { CircularProgress } from "@clockwise/web-commons/src/components/circular-progress";
import { track, TrackingEvents } from "@util/analytics.util";
import { getBulkExperiments } from "@util/experiment.util";
import { recordImpression } from "@mutations";
import { EXPERIMENT_FLAGS } from "@util/experiment.util";
import { OnboardingOrgBulkInvite } from "../org-invite-bulk/OnboardingOrgBulkInvite";
import { OnboardingOrgBulkInviteQueryRenderer } from "../org-invite-bulk/OnboardingOrgBulkInviteQueryRenderer";
import { IViewer, IOrg, SuggestedOrgInviteReason } from "@schema";

// material-ui
import { InputAdornment, List, TextField } from "@material-ui/core";
import { Check, Clear, Search } from "@material-ui/icons";
import { Button } from "@clockwise/design-system";

//////////////////
// TYPES
//////////////////
export type OnboardingOrgInviteProps = {
  viewer: IViewer;
  org: IOrg;
  showCurrentUser: boolean;
  marginCorrect?: number;
  onAddInvitees: (attendeesInviteInput: InviteAttendeesInput[]) => void;
  selectedCalendarIds: string[];
  onInvitesLoaded?: () => void;
  onRemoveAllInvitees: () => void;
  relay: RelayRefetchProp;
};

export type InviteAttendeesInput = { calendarId: string; added: boolean; isSuggested: boolean };

export type AugmentedPerson = {
  targetCalendarId: string;
  givenName: string;
  familyName: string;
  externalImageUrl: string;
  isSuggested: boolean;
  isUser: boolean;
  isYou: boolean;
  isPending: boolean;
  numberOfSharedMeetings: number;
  numberOfOrganizedMeetings: number;
  inviteReason: SuggestedOrgInviteReason;
  totalScore: number;
};

export type AugmentedPersonMap = {
  [k: string]: AugmentedPerson;
};

//////////////////
// COMPONENT
//////////////////

const debouncedQueryOnSearch = debounce((refetch, orgId, newQuery) => {
  refetch({
    orgRelayId: orgId,
    queryInput: newQuery,
    excludeCalendars: [],
  });
}, 500);

export const OnboardingOrgInviteComponent = ({
  viewer,
  org,
  showCurrentUser,
  marginCorrect,
  onAddInvitees,
  onRemoveAllInvitees,
  selectedCalendarIds,
  onInvitesLoaded,
  relay,
}: OnboardingOrgInviteProps) => {
  const [currentPersons, setCurrentPersons] = useState<AugmentedPersonMap>({});
  const [query, setQuery] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [eventQueryFinished, setEventQueryFinished] = useState<boolean>(false);
  const [recommendedEvents, setRecommendedEvents] = useState<{
    [keyof: string]: AugmentedPersonMap;
  }>({});
  // This state only exists to satify the event checkbox being filled in or not, the attendees of the event are added to the invite list individually
  const [eventsSelectedForInvite, setEventsSelectedForInvite] = useState<string[]>([]);

  const isInBulkInvitesExperiment = getBulkExperiments(org).BulkInvitesOnboarding === true;

  useEffect(() => {
    const newCurrentPersons = getAugmentedPersons({
      org,
      showCurrentUser,
      query,
    });
    if (Object.keys(newCurrentPersons).length && loading) {
      setLoading(false);
    }
    setCurrentPersons(newCurrentPersons);
  }, [org]);

  useEffect(() => {
    setLoading(false);
    if (onInvitesLoaded) {
      onInvitesLoaded();
    }

    const suggestedPersons = sortPersons(currentPersons)
      .slice(0, 20) // for perf reasons, only render 10
      .filter((calId) => currentPersons[calId].isSuggested && !currentPersons[calId].isUser);
    track(TrackingEvents.ONBOARDING.ONBOARDING_INVITES_SUGGESTED_INVITEES_COUNT, {
      count: suggestedPersons.length,
    });
    // personalized links experiment
    recordImpression(
      relay.environment,
      org,
      EXPERIMENT_FLAGS.personalizedInviteLinks,
      () => null,
      () => null,
    );
  }, [currentPersons]);

  useEffect(() => {
    debouncedQueryOnSearch(relay.refetch, org.id, query);
  }, [query]);

  // ~-~-~-~-~-~-~-
  // Handlers
  // ~-~-~-~-~-~-~-
  const onSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
  };

  const onSelectAll = (selectAll: boolean) => {
    const trackingString = selectAll
      ? TrackingEvents.ONBOARDING.ONBOARDING_INVITES_SELECT_ALL_CLICKED
      : TrackingEvents.ONBOARDING.ONBOARDING_INVITES_UNSELECT_ALL_CLICKED;
    track(trackingString);

    if (!selectAll) {
      onRemoveAllInvitees();
      setEventsSelectedForInvite([]);
      return;
    } else {
      let personsSorted: AugmentedPersonMap = cloneDeep(currentPersons);
      const recommendedEventsDupe = cloneDeep(recommendedEvents);
      const selectedTeams = [...eventsSelectedForInvite];
      for (const event of Object.keys(recommendedEventsDupe)) {
        if (eventsSelectedForInvite.indexOf(event) <= -1) {
          selectedTeams.push(event);
        }
        personsSorted = merge(recommendedEventsDupe[event], personsSorted);
      }
      const attendeesToAddOrRemove: InviteAttendeesInput[] = [];
      sortPersons(personsSorted).forEach((primaryEmail: string) => {
        // select/unselect if they are not already a user
        if (!personsSorted[primaryEmail].isUser && !personsSorted[primaryEmail].isPending) {
          attendeesToAddOrRemove.push({
            calendarId: primaryEmail,
            added: selectAll,
            isSuggested: personsSorted[primaryEmail].isSuggested,
          });
        }
      });
      onAddInvitees(attendeesToAddOrRemove);
      setEventsSelectedForInvite(selectedTeams);
    }
  };

  const stopQueryRendering = (eventsToAttendeeMap?: { [keyof: string]: AugmentedPersonMap }) => {
    if (eventsToAttendeeMap) {
      setRecommendedEvents(eventsToAttendeeMap);
    }
    setEventQueryFinished(true);
  };

  const onAddInviteesFromEventGroup = (
    eventGroupName: string,
    attendeesInviteInput: InviteAttendeesInput[],
  ) => {
    track(TrackingEvents.ONBOARDING.ONBOARDING_INVITES_BULK_INVITE_SELECTED, {
      numberOfAttendees: attendeesInviteInput.length,
      eventName: eventGroupName,
    });
    const eventsSelectedCopy = [...eventsSelectedForInvite];
    const indexOfEventInExistingState = eventsSelectedCopy.indexOf(eventGroupName);
    if (indexOfEventInExistingState > -1) {
      eventsSelectedCopy.splice(indexOfEventInExistingState, 1);
    } else {
      eventsSelectedCopy.push(eventGroupName);
    }
    setEventsSelectedForInvite(eventsSelectedCopy);
    onAddInvitees(attendeesInviteInput);
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  const renderPersons = () => {
    // Wait until bulk invite query renders before showing everything to avoid ui shifting
    if (!eventQueryFinished && isInBulkInvitesExperiment) {
      return (
        <div className="cw-flex cw-justify-center cw-mt-10">
          <CircularProgress />
        </div>
      );
    }
    const persons = currentPersons;

    if (!Object.keys(persons).length) {
      return <div className="cw-text-gray-500 cw-py-3">No results found</div>;
    }
    // don't include users who have already joined
    const newPersons = Object.keys(persons)
      .filter((key) => !persons[key].isUser)
      .reduce((obj: AugmentedPersonMap, key: string) => {
        obj[key] = persons[key];
        return obj;
      }, {});

    return sortPersons(newPersons)
      .slice(0, 20) // for perf reasons, only render 10
      .map((calId, i) => {
        return (
          <OrgInviteCheckbox
            key={i}
            people={[{ ...persons[calId] }]}
            isChecked={selectedCalendarIds.indexOf(calId) > -1}
            onCheck={onAddInvitees}
          />
        );
      });
  };

  if (loading) {
    return (
      <div className="cw-flex cw-justify-center cw-mt-10">
        <CircularProgress />
      </div>
    );
  }

  const marginStyles = marginCorrect
    ? {
        marginLeft: marginCorrect,
        marginRight: marginCorrect,
      }
    : {};

  const showSelectAll = isAtLeastOnePersonNotSelectedAndNotUser(
    currentPersons,
    selectedCalendarIds,
  );

  return (
    <div>
      <div className="cw-flex cw-items-baseline cw-flex-row cw-justify-between cw-gap-1">
        <TextField
          label="Search for teammates"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search className="cw-w-5 cw-h-5" />
              </InputAdornment>
            ),
          }}
          value={query}
          onChange={onSearch}
          className="cw-w-[300px]"
        />
        {getBulkExperiments(org).PersonalizedInviteLinks === true ? (
          <CopyPersonalInviteLinkBtn org={org} user={viewer.user} />
        ) : (
          <Button
            variant="text"
            startIcon={showSelectAll ? Check : Clear}
            onClick={() => onSelectAll(showSelectAll)}
          >
            {showSelectAll ? "Select all" : "Unselect all"}
          </Button>
        )}
      </div>
      <List style={{ ...marginStyles }}>
        {isInBulkInvitesExperiment && !eventQueryFinished ? (
          <OnboardingOrgBulkInviteQueryRenderer
            org={org}
            relay={relay}
            onEventsQueryComplete={stopQueryRendering}
          />
        ) : null}
        {!query && recommendedEvents && isInBulkInvitesExperiment && (
          <OnboardingOrgBulkInvite
            eventsMap={recommendedEvents}
            onAddInvitees={onAddInviteesFromEventGroup}
            eventsSelectedForInvite={eventsSelectedForInvite}
          />
        )}
        {renderPersons()}
      </List>
    </div>
  );
};

//////////////////
// REDUX
//////////////////
const reduxContainer = connect()(OnboardingOrgInviteComponent);

//////////////////
// RELAY
//////////////////
export const OnboardingOrgInvite = createRefetchContainer(reduxContainer, fragments, refetchQuery);
